/**
 * 错误上报工具
 * - 现在是 stub：控制台打印 + 占位 Promise
 * - 后端 /sys/error/report 接口就绪后，把 reportError 里的实现换成：
 *   return fetcher('/sys/error/report', 'POST', { body: { message, stack, info, url, ts } })
 * - 调用方约定：所有错误（ErrorBoundary / window.onerror / unhandledrejection）都走这一个出口
 */
import { fetcher } from '@src/utils/fetcher'

export interface ErrorReportPayload {
	/** 错误消息（Error.message） */
	message: string
	/** 错误堆栈（Error.stack），可能很长 */
	stack?: string
	/** 附加信息：组件堆栈 / 触发位置 / 自定义上下文 */
	info?: Record<string, unknown>
	/** 错误发生时的 URL */
	url?: string
	/** 客户端时间戳（ms） */
	ts?: number
}

const pendingQueue: ErrorReportPayload[] = []
let isSending = false

/**
 * 上报错误到后端
 * - 失败 / 离线时入队，等下次调用时重试
 * - 后端接口就绪后只需替换 `send` 函数
 */
export async function reportError(payload: ErrorReportPayload): Promise<void> {
	pendingQueue.push(payload)
	// 异步串行发送（不阻塞调用方）
	void flush()
}

/** 串行消费队列；后端接口未就绪时仅打印 */
async function flush(): Promise<void> {
	if (isSending) return
	isSending = true
	try {
		while (pendingQueue.length > 0) {
			const payload = pendingQueue[0]
			try {
				await send(payload)
			} catch (e) {
				// 发送失败就保留在队首，下次再试
				console.warn('[ErrorReporter] send failed, will retry next call', e)
				break
			}
			pendingQueue.shift()
		}
	} finally {
		isSending = false
	}
}

/** 实际发送 —— 后端 /sys/error/report 就绪后，替换这里为 fetcher */
async function send(payload: ErrorReportPayload): Promise<void> {
	// TODO: 后端接口 ready 后替换为：
	//   return fetcher('/sys/error/report', 'POST', { body: payload })
	// 当前 stub：只 console.error，方便开发期看到
	console.error('[ErrorReporter] (stub)', payload)
}

/** 便捷：从 Error + ErrorInfo 直接构造 payload 并上报 */
export function reportFromBoundary(error: Error, info?: { componentStack?: string }): void {
	void reportError({
		message: error?.message || String(error),
		stack: error?.stack,
		info: info ? { componentStack: info.componentStack } : undefined,
		url: typeof window !== 'undefined' ? window.location.href : undefined,
		ts: Date.now()
	})
}

/** 便捷：从全局 error 事件构造 payload */
export function reportFromWindowError(event: ErrorEvent): void {
	void reportError({
		message: event.message || event.error?.message || 'Unknown error',
		stack: event.error?.stack,
		info: {
			filename: event.filename,
			lineno: event.lineno,
			colno: event.colno
		},
		url: typeof window !== 'undefined' ? window.location.href : undefined,
		ts: Date.now()
	})
}

/** 便捷：从 Promise 拒绝构造 payload */
export function reportFromRejection(reason: unknown): void {
	const err = reason instanceof Error ? reason : new Error(String(reason))
	void reportError({
		message: err.message,
		stack: err.stack,
		url: typeof window !== 'undefined' ? window.location.href : undefined,
		ts: Date.now()
	})
}
